import type { Variants } from '@amplitude/experiment-js-client';

import { getFeatureFlagCookieOverrides } from '@liveauctioneers/hammer-ui-core/featureFlags/devTools/getFeatureFlagCookieOverrides';

import type { FeatureFlags } from '@/types/FeatureFlags';

import { AmplitudeExperiments, getKnownVariantValues } from '../AmplitudeExperiments';
import { areDevOverridesAllowed, type DevOverrideDeployment } from './forcedExperimentOverrides';

/** Request cookies as both sides read them: unparsed strings keyed by cookie name. */
export type FeatureFlagCookies = Record<string, string | undefined>;

const TRUTHY_VALUES = new Set(['on', 'treatment']);

export const variantsToFeatureFlags = (variants: Variants): Record<string, boolean> =>
    Object.fromEntries(
        Object.entries(variants).map(([key, variant]) => [key, TRUTHY_VALUES.has(variant?.value ?? '')])
    );

/**
 * An on/off cookie can't pick between several treatments, so it only overrides experiments with
 * exactly control + one treatment; multi-variant experiments use ?forceExperiment instead.
 */
const BOOLEAN_EXPERIMENT_VARIANT_COUNT = 2; // control + one treatment
const CONTROL_VARIANT = 'control';

const NON_BOOLEAN_FLAG_KEYS = new Set<string>(
    Object.values(AmplitudeExperiments).filter(
        (flagKey) => getKnownVariantValues(flagKey)?.length !== BOOLEAN_EXPERIMENT_VARIANT_COUNT
    )
);

export const isBooleanOverridable = (flagKey: string): boolean => !NON_BOOLEAN_FLAG_KEYS.has(flagKey);

/** Dev-tools cookie overrides for `flags`, minus the multi-variant experiments. */
export const getBooleanCookieOverrides = (
    flags: Record<string, boolean>,
    cookies: FeatureFlagCookies
): Record<string, boolean> =>
    Object.fromEntries(
        Object.entries(getFeatureFlagCookieOverrides(flags, cookies)).filter(([flagKey]) =>
            isBooleanOverridable(flagKey)
        )
    );

/**
 * The feature flags every consumer sees, on the server and the client alike: the feature-flag API
 * payload, the Amplitude-derived flags on top, and the dev-tools cookie overrides (Cmd+/ overlay)
 * on top of both. Both sides must call this with the same inputs, or server and client render
 * different trees and hydration fails.
 *
 * Only cookies named after a flag key from the first two layers count as overrides.
 */
export const mergeFeatureFlags = (
    baseFeatureFlags: FeatureFlags,
    variants: Variants,
    cookies: FeatureFlagCookies = {}
): FeatureFlags => {
    const featureFlags = {
        ...baseFeatureFlags,
        ...variantsToFeatureFlags(variants),
    };

    return {
        ...featureFlags,
        ...getBooleanCookieOverrides(featureFlags, cookies),
    };
};

/**
 * The cookies that may act as dev-tools flag overrides: none on prod, so visitors cannot flip
 * flags there (same rule as forceExperiment). Server and client must both pass their cookies
 * through this before handing them to mergeFeatureFlags or the Amplitude provider.
 */
export const getFeatureFlagCookies = (
    deployment: DevOverrideDeployment,
    cookies: FeatureFlagCookies
): FeatureFlagCookies => (areDevOverridesAllowed(deployment) ? cookies : {});

/** The variant an on/off cookie stands for: the experiment's single treatment when on, control when off. */
const getCookieVariantValue = (flagKey: string, isOn: boolean): string => {
    const knownVariantValues = getKnownVariantValues(flagKey);

    if (!knownVariantValues) {
        // a plain Amplitude flag outside the experiment registry
        return isOn ? 'on' : 'off';
    }

    return isOn ? (knownVariantValues.find((value) => value !== CONTROL_VARIANT) ?? CONTROL_VARIANT) : CONTROL_VARIANT;
};

/**
 * Dev-tools cookie overrides as variants, so every reader of `variants` (useAmplitudeFeatureFlag,
 * AppHammerUIProvider) sees them. Only keys the user already has a variant for are overridden.
 */
export const getCookieVariantOverrides = (variants: Variants, cookies: FeatureFlagCookies): Variants =>
    Object.fromEntries(
        Object.entries(getBooleanCookieOverrides(variantsToFeatureFlags(variants), cookies)).map(([flagKey, isOn]) => [
            flagKey,
            { value: getCookieVariantValue(flagKey, isOn) },
        ])
    );
